// "Under the lens" panel pieces (DESIGN_SPEC §8 score meter, §10.5, §11.4.2; RESTYLE §1 hero numerals).
//   <Lens label eyebrow? name? className?>…</Lens>   aside.lens; add "rhead-lens" (report header) or "cell s4 side" via className
//   <LensScore value of? verdict? clock? pending? />   the ONE hero numeral per view (mono 300, tabular): score in verdict colour, or an ink clock
//   <Meter value verdict? label? />                    score meter: fill = verdict colour on a 16% track, ticks at 25 / 60, band labels
//   <Meter value clock={{ start, now?, end }} />        live run clock (T0 → timeout), value = elapsed % of the timeout
//   <TopSignals items={[{ sev, title, tech? }]} />      at most 4 rows: severity glyph, Inter Tight title, technique id right
import type { ReactNode } from "react";
import { Sev, VERDICT_COLOR, verdictForScore, type SevLevel, type VerdictLabel } from "./Glyphs";
import { cx, vars } from "./cx";

export function Lens({ label, eyebrow, name, className, children }: { label: string; eyebrow?: ReactNode; name?: ReactNode; className?: string; children?: ReactNode }) {
  return (
    <aside className={cx("lens", className)} aria-label={label}>
      {eyebrow && <p className="lens-eyebrow">{eyebrow}</p>}
      {name && <p className="lens-name">{name}</p>}
      {children}
    </aside>
  );
}

export function LensScore({
  value,
  of,
  verdict,
  clock,
  pending,
}: {
  value: ReactNode;
  of?: ReactNode;
  verdict?: VerdictLabel;
  /** a changing clock: ink (tabular mono digits never jitter) */
  clock?: boolean;
  /** no score yet: faint */
  pending?: boolean;
}) {
  return (
    <div className="lens-score" style={verdict ? vars({ "--vc": VERDICT_COLOR[verdict] }) : undefined}>
      <span className={cx("n", clock && "clock", pending && "pending")}>{value}</span>
      {of !== undefined && <span className="of">{of}</span>}
    </div>
  );
}

const BANDS = ["benign", "suspicious", "malicious"] as const;

export function Meter({
  value,
  verdict,
  label = "Score",
  clock,
  valueText,
}: {
  /** 0–100: the score, or for a clock the elapsed share of the timeout */
  value: number;
  verdict?: VerdictLabel;
  label?: string;
  clock?: { start: ReactNode; now?: ReactNode; end: ReactNode };
  valueText?: string;
}) {
  const v = Math.max(0, Math.min(100, value));
  if (clock) {
    const at = Math.max(14, Math.min(86, v));
    return (
      <div className="meter meter--clock" style={vars({ "--v": v })} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(v)} aria-valuetext={valueText} aria-label={label}>
        <div className="meter-track">
          <div className="meter-fill" />
        </div>
        <div className="meter-scale">
          <span>{clock.start}</span>
          {clock.now !== undefined && (
            <span className="absolute top-0 -translate-x-1/2 text-live-text" style={{ left: `${at}%` }}>
              {clock.now}
            </span>
          )}
          <span>{clock.end}</span>
        </div>
      </div>
    );
  }
  const band = verdict && verdict !== "pending" ? verdict : verdictForScore(v);
  const vc = VERDICT_COLOR[verdict ?? band];
  return (
    <div
      className="meter"
      style={vars({ "--v": v, "--vc": vc })}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={v}
      aria-valuetext={valueText ?? `${v} of 100, ${band}`}
      aria-label={label}
    >
      <div className="meter-track">
        <div className="meter-fill" />
        <i className="meter-tick" style={{ left: "25%" }} />
        <i className="meter-tick" style={{ left: "60%" }} />
      </div>
      <div className="meter-bands" aria-hidden="true">
        {BANDS.map((b) => (
          <span key={b} className={b === band && verdict !== "pending" ? "on" : undefined}>
            {b}
          </span>
        ))}
      </div>
    </div>
  );
}

type TopSignal = { sev: SevLevel; title: ReactNode; tech?: ReactNode };

export function TopSignals({ items, heading = "Top signals" }: { items: TopSignal[]; heading?: ReactNode }) {
  if (!items.length) return null;
  return (
    <>
      <p className="lens-eyebrow mt-3">{heading}</p>
      <ul className="kv -mt-1">
        {items.slice(0, 4).map((s, i) => (
          <li key={i} className="flex items-baseline gap-2.5 border-t border-rule-cell py-2">
            <Sev level={s.sev} label={false} />
            <span className="sig-title min-w-0">
              {s.title}
            </span>
            {s.tech && <span className="tech-id ml-auto">{s.tech}</span>}
          </li>
        ))}
      </ul>
    </>
  );
}
